/**
 * @fileoverview Helper para generar archivos Excel del Informe de Operarios
 */

import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

/**
 * Genera un archivo Excel con los logs de tareas de operarios/proveedores filtrados
 * @param {Array} logs - Array de logs de log_plan_centro_costo (con expand de orden, centro_costo, operario, proceso_proveedor)
 * @returns {void}
 */
export function generateExcel(logs) {
  try {
    if (!logs || logs.length === 0) {
      throw new Error('No hay logs para exportar');
    }

    // Definir headers
    const headers = [
      'Número de Orden',
      'Operario/Proveedor',
      'Fecha de Inicio',
      'Fecha de Fin',
      'Nombre del Centro de Costo'
    ];

    // Transformar datos al formato requerido
    const rows = logs.map(log => {
      // Obtener número de orden
      const numeroOrden = log.expand?.orden?.numero || 'N/A';

      // Obtener operario o proveedor
      let operarioProveedor = 'Sin asignar';
      if (log.expand?.operario) {
        const nombreOperario = log.expand.operario.name || log.expand.operario.email || 'N/A';
        const estaActivo = log.expand.operario.activo !== false; // Default to true if undefined
        operarioProveedor = estaActivo ? nombreOperario : `${nombreOperario} (desactivado)`;
      } else if (log.expand?.proceso_proveedor) {
        operarioProveedor = log.expand.proceso_proveedor.nombre || 'N/A';
      }

      // Obtener fechas
      const fechaInicio = log.inicio ? new Date(log.inicio).toLocaleDateString() : '';
      const fechaFin = log.fin ? new Date(log.fin).toLocaleDateString() : '';

      // Obtener nombre del centro de costo
      const centroCosto = log.expand?.centro_costo?.nombre || 'N/A';

      return [
        numeroOrden,
        operarioProveedor,
        fechaInicio,
        fechaFin,
        centroCosto
      ];
    });

    // Crear hoja de trabajo (array of arrays)
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Verificar que se creó correctamente
    if (!ws || !ws['!ref']) {
      throw new Error('Error al crear la hoja de trabajo Excel');
    }

    // Aplicar estilos y formato
    applyStyles(ws);

    // Crear libro de trabajo
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tareas Operarios');

    // Generar archivo y descargar usando file-saver
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileName = `Informe_Operarios_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
}

/**
 * Aplica estilos a la hoja de trabajo
 * @param {Object} ws - Hoja de trabajo
 */
function applyStyles(ws) {
  if (!ws || !ws['!ref']) {
    return; // No aplicar estilos si la hoja no es válida
  }

  const range = XLSX.utils.decode_range(ws['!ref']);
  const numRows = range.e.r + 1;
  const numCols = range.e.c + 1;

  // Aplicar estilos a todas las celdas
  for (let row = 0; row < numRows; row++) {
    for (let col = 0; col < numCols; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      
      if (!ws[cellRef]) {
        continue; // Saltar celdas vacías
      }

      // Estilos para headers (primera fila)
      if (row === 0) {
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4472C4' } },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        };
      } else {
        // Estilos para datos
        ws[cellRef].s = {
          alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
            right: { style: 'thin', color: { rgb: 'CCCCCC' } }
          }
        };
      }
    }
  }

  // Configurar anchos de columna
  ws['!cols'] = [
    { wch: 15 }, // Número de Orden
    { wch: 25 }, // Operario/Proveedor
    { wch: 15 }, // Fecha de Inicio
    { wch: 15 }, // Fecha de Fin
    { wch: 25 }  // Nombre del Centro de Costo
  ];

  // Configurar altura de filas y wrapText
  ws['!rows'] = [];
  for (let row = 0; row < numRows; row++) {
    ws['!rows'][row] = { hpt: row === 0 ? 30 : 20, hpx: row === 0 ? 30 : 20 };
  }
}

